/**
 * Display-only interpretation of the org book-ahead window for the public
 * booking calendar's empty state. Decides whether an empty month is explained
 * by the booking horizon — it never influences which dates are bookable
 * (that's `booking-window.ts` + the availability gate).
 *
 * The claim is deliberately conservative: only a month that lies entirely
 * beyond `today + bookingWindowDays` is attributed to the horizon. An empty
 * month that overlaps the horizon could instead be availability gaps, blocked
 * dates, or capacity — attributing those to the window would misdiagnose.
 */

export type BookingHorizonExplanation = {
  /** Last org-local date (YYYY-MM-DD) that can be inside the booking window. */
  horizonEndYmd: string;
};

function parseYmdUtc(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function formatYmdUtc(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

/**
 * Returns the horizon explanation when the displayed month starts strictly
 * after the last bookable date, and `null` whenever the horizon is not
 * provably the limiting reason (month overlaps the window, or is in the past).
 *
 * `todayYmd` must be today in the ORG timezone (not the viewer's) so the
 * boundary agrees with the server's window math in `booking-window.ts`.
 */
export function explainEmptyMonthViaHorizon(input: {
  /** Year/month the calendar is displaying. `month` is 1-based. */
  displayedYear: number;
  displayedMonth: number;
  /** Today's date in the org timezone, YYYY-MM-DD. */
  todayYmd: string;
  bookingWindowDays: number;
}): BookingHorizonExplanation | null {
  const { displayedYear, displayedMonth, todayYmd, bookingWindowDays } = input;
  if (!Number.isFinite(displayedYear) || !Number.isFinite(displayedMonth)) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(todayYmd)) return null;

  // Same floor the filters endpoint applies (`Math.max(1, …)`).
  const windowDays = Math.max(1, Math.floor(bookingWindowDays));

  const horizonEnd = parseYmdUtc(todayYmd);
  horizonEnd.setUTCDate(horizonEnd.getUTCDate() + windowDays);
  const horizonEndYmd = formatYmdUtc(horizonEnd);

  const monthStartYmd = `${displayedYear}-${String(displayedMonth).padStart(2, "0")}-01`;

  // A month at or before today is never "beyond the horizon" — its emptiness
  // has some other cause (or the dates are simply in the past).
  if (monthStartYmd <= todayYmd) return null;
  if (monthStartYmd <= horizonEndYmd) return null;

  return { horizonEndYmd };
}
